import { UserRow } from 'components/tables/data/user/types';
import { Permission } from 'model/PermissionDataContext';

export interface UserPermissionsFilterModel {
  filter: string[];
}

interface PermissionFilterOption {
  value: string;
  label: string;
  group: string;
}

/**
 * Normalizes text for accent-insensitive permission search.
 */
function normalizePermissionFilterSearchValue(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
    .trim();
}

/**
 * Normalizes unknown filter values to a stable permission-slug array.
 */
export function normalizeUserPermissionsFilterValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(String).filter(Boolean);
  }

  if (value === null || value === undefined || value === -1 || value === '-1' || value === '') {
    return [];
  }

  return [String(value)];
}

/**
 * Returns whether the user has every selected permission anywhere.
 */
export function userHasSelectedPermissionsAnywhere(user: UserRow, selectedSlugs: string[]): boolean {
  if (selectedSlugs.length === 0) {
    return true;
  }

  const anywherePermissions = user.permissions.anywhere as Record<string, boolean | undefined>;

  return selectedSlugs.every((slug) => anywherePermissions?.[slug] === true);
}

/**
 * Builds grouped multi-select options from permission metadata.
 */
export function buildPermissionFilterOptions(permissions: Permission[]): PermissionFilterOption[] {
  const uniquePermissions = new Map<string, Permission>();

  permissions.forEach((permission) => {
    if (!uniquePermissions.has(permission.slug)) {
      uniquePermissions.set(permission.slug, permission);
    }
  });

  return Array.from(uniquePermissions.values())
    .sort((left, right) => {
      if (left.permissionGroup !== right.permissionGroup) {
        return left.permissionGroup.localeCompare(right.permissionGroup);
      }

      return left.permissionName.localeCompare(right.permissionName);
    })
    .map((permission) => ({
      value: permission.slug,
      label: permission.permissionName,
      group: permission.permissionGroup,
    }));
}

/**
 * Filters permission options by full-text permission-name search.
 */
export function filterPermissionFilterOptions(
  options: PermissionFilterOption[],
  search: string
): PermissionFilterOption[] {
  const normalizedSearch = normalizePermissionFilterSearchValue(search);

  if (!normalizedSearch) {
    return options;
  }

  const tokens = normalizedSearch.split(/\s+/).filter(Boolean);

  return options.filter((option) => {
    const normalizedLabel = normalizePermissionFilterSearchValue(option.label);
    return tokens.every((token) => normalizedLabel.includes(token));
  });
}
